import { compact } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AutomationHubGuidedAiPage from '~/components/AutomationHub/AutomationHubGuidedAiPage/AutomationHubGuidedAiPage';
import AutomationHubManagement from '~/components/AutomationHub/AutomationHubManagement/AutomationHubManagement';
import AutomationHubQuickAiPage from '~/components/AutomationHub/AutomationHubQuickAiPage/AutomationHubQuickAiPage';
import withHover from '~/components/Shared/HOCs/withHover/withHover';
import BackIcon from '~/components/Shared/Icons/BackIcon';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Tabs } from '~/eds/src/components/Tabs/Tabs';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { AutomationStatusType } from '~/enums/AutomationStatusType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { QueryParamType } from '~/enums/QueryParamType';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

const Page = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const model = useSelector(slices.automation.selectors.selectAutomationModel);

  const clause = useSelector(
    slices.automation.selectors.selectAutomationClause,
  );
  const clauseName = getPageSearchQueryByKey(
    QueryParamType.Clause,
    clause.name,
  );

  const hasAutomationSmartClauseFull = useHasFeatureFlag(
    FeatureFlagType.AutomationSmartClauseFull,
  );

  const TABS = compact([
    clauseName && {
      label: intl.formatMessage({
        id: 'AutomationDetail.quickAiTab',
        defaultMessage: 'Quick AI',
        description: 'Tab label for Quick AI',
      }),
      value: 'QUICK_AI',
    },
    hasAutomationSmartClauseFull && {
      label: intl.formatMessage({
        id: 'AutomationDetail.guidedAiTab',
        defaultMessage: 'Guided AI',
        description: 'Tab label for Guided AI',
      }),
      value: 'GUIDED_AI',
    },
  ]);

  const defaultTab = useMemo(() => {
    if (model.showCreating) {
      return 'GUIDED_AI';
    } else {
      return 'QUICK_AI';
    }
  }, [model]);

  const [currentTab, setCurrentTab] = useState('');

  useEffect(() => {
    setCurrentTab(defaultTab);
  }, [defaultTab]);

  const BackIconWithHover = withHover(BackIcon);
  const { navigate } = useRouting();

  const isLongClauseName = clauseName?.length > 64;
  const title = isLongClauseName
    ? `${clauseName?.substring(0, 64)}...`
    : clauseName;

  const onBackToClausePage = () => {
    dispatch(slices.automation.actions.resetAutomationStates());
    dispatch(
      slices.automation.actions.setAutomationClause({
        ...clause,
        name: '',
        status: AutomationStatusType.ACTIVE,
      }),
    );
    navigate(-1);
  };

  return (
    <ContentContainer>
      <FlexLayout flexDirection="column" space={8} mb={25}>
        <FlexLayout justifyContent="space-between">
          <FlexLayout space={2}>
            <button onClick={onBackToClausePage}>
              <BackIconWithHover />
            </button>
            {isLongClauseName ? (
              <Tooltip tooltip={clauseName} placement="bottom">
                <Text variant="2xl-spaced-bold" color="gray-700">
                  {title}
                </Text>
              </Tooltip>
            ) : (
              <Text variant="2xl-spaced-bold" color="gray-700">
                {title}
              </Text>
            )}
          </FlexLayout>
        </FlexLayout>
        <Tabs
          enableContentPadding={false}
          selectedTab={currentTab}
          tabs={TABS}
          onSelectTab={setCurrentTab}
        />
        {currentTab === 'QUICK_AI' && <AutomationHubQuickAiPage />}
        {currentTab === 'GUIDED_AI' && <AutomationHubGuidedAiPage />}
        <AutomationHubManagement />
      </FlexLayout>
    </ContentContainer>
  );
};

export default Page;
